// components/consumers/ConsumerDetail.tsx

import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  Activity,
  AlertCircle,
  Banknote,
  Crown,
  File,
  Mail,
  MessageSquareIcon,
  Pencil,
  Plug,
  Send,
  Settings,
  User,
  Phone,
  MapPin,
} from "lucide-react";
// Import the new componentized tabs
import { ComplaintsTab } from "./components/ComplaintsTab";
import { ConnectionsTab } from "./components/connection/ConnectionsTab";
import { DocumentsTab } from "./components/DocumentsTab";
import { FinancialTab } from "./components/FinancialTab";
import { OverviewTab } from "./components/OverviewTab";
import { RequestsTab } from "./components/RequestsTab";
import { ServiceTab } from "./components/ServiceTab";

// Import API service and types
import { Consumer } from "@features/cx/consumerManagement/types";
import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import { useConsumerWithBillData } from "../../hooks";
import { QuickActionMenu } from "./components/QuickActionMenu";
import { ConnectionLanding } from "./components/connection/ConnectionLanding";
import { formatTerritoryAddress } from "@shared/utils/address";

const ConsumerDetail = () => {
  const { consumerNo } = useParams<{ consumerNo: string }>();
  const remoteUtilityId = useRemoteUtilityId();

  const {
    data: consumerInfo,
    isLoading: loading,
    error,
    refetch: fetchConsumerData,
  } = useConsumerWithBillData(consumerNo!, remoteUtilityId);

  const consumerID = consumerInfo?.id.toString() || "";
  console.log("consumerInfo", consumerInfo);

  const urlMapping = {
    overview: "overview",
    financial: "financial",
    connections: "connections",
    service: "service",
    complaints: "complaints",
    requests: "requests",
    communication: "communication",
    documents: "documents",
    notes: "notes",
    activity: "activity",
  };
  const tabComponents = {
    overview: {
      label: "Overview",
      icon: <User className="h-4 w-4" />,
      component: (
        <OverviewTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    financial: {
      label: "Financial",
      icon: <Banknote className="h-4 w-4" />,
      component: (
        <FinancialTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
    connections: {
      label: "Connections",
      icon: <Plug className="h-4 w-4" />,
      component: (
        <ConnectionLanding
          consumerNo={consumerNo}
          consumerID={consumerID}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
    service: {
      label: "Service",
      icon: <Settings className="h-4 w-4" />,
      component: (
        <ServiceTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    complaints: {
      label: "Complaint History",
      icon: <AlertCircle className="h-4 w-4" />,
      component: (
        <ComplaintsTab
          consumerId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    requests: {
      label: "Request History",
      icon: <Send className="h-4 w-4" />,
      component: (
        <RequestsTab
          consumerId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    communication: {
      label: "Communication",
      icon: <MessageSquareIcon className="h-4 w-4" />,
      subTabsListClassName: "grid w-full grid-cols-3",
      component: (
        <RequestCommunications
          entityId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
          email={consumerInfo?.email}
          phone={consumerInfo?.contactNumber || ""}
          action="consumer"
        />
      ),
    },
    documents: {
      label: "Documents",
      icon: <File className="h-4 w-4" />,
      component: (
        <DocumentsTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    notes: {
      label: "Notes",
      icon: <Pencil className="h-4 w-4" />,
      component: (
        <NotesTab
          requestId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
          source="consumer"
        />
      ),
    },
    activity: {
      label: "Activity",
      icon: <Activity className="h-4 w-4" />,
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId)}
          entityId={consumerInfo?.id.toString()}
          actionType="nsc"
        />
      ),
    },
  };

  return (
    <div className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="flex items-center justify-between">
        <div>
          <p className="page-title">Consumer Profile</p>
          <p className="page-subtitle">
            Manage consumer account information and services
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <Button 
            variant="outline"
            size="sm"
            className="flex items-center gap-1 border-blue-200 hover:bg-blue-50"
            onClick={() => setUpdateProfileOpen(true)}
          >
            <Pencil className="h-3.5 w-3.5 text-blue-600" />
            Update Profile
          </Button> */}
          <QuickActionMenu
            consumerId={consumerInfo?.id.toString()}
            remoteUtilityId={Number(remoteUtilityId)}
            consumerData={consumerInfo}
          />
        </div>
      </div>

      {/* Alerts Section */}
      {/* {consumerInfo.hasAlerts && (
        <ConsumerAlerts alerts={consumerInfo.alerts} />
      )} */}
      {/* Consumer Summary Card */}
      <ConsumerSummaryCard consumer={consumerInfo} />

      {/* Tabs Section */}
      <Tabs
        defaultValue="overview"
        className="w-full"
        tabsListClassName="grid grid-cols-10 w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0} // Top-level tabs
      />
    </div>
  );
};

// Sub-component for Consumer Summary Card
interface ConsumerSummaryCardProps {
  consumer: Consumer;
}

const ConsumerSummaryCard: React.FC<ConsumerSummaryCardProps> = ({
  consumer,
}) => {
  const CURRENCY = useCurrency();
  return (
    <div className="grid gap-6">
      <Card className="overflow-hidden border-t-4 border-t-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md">
        <CardContent className="p-0">
          <div className="bg-blue-50/70 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {consumer?.firstName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">
                      {consumer?.firstName} {consumer?.lastName}
                    </h2>
                    {consumer?.isVip && (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 flex items-center gap-1">
                        <Crown className="h-3 w-3" />
                        VIP
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-muted-foreground">
                      {consumer?.consumerNo}
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-green-100 text-green-800 border-green-200 shadow-sm"
                    >
                      {consumer?.statusDisplay}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">
                    Current Balance
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {CURRENCY} {consumer?.billData?.outstandingBalance}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last Billed: {consumer?.billData?.createdDate}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 border-t">
            {/* Contact Information Card */}
            <Card className="shadow-md bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-indigo-700">
                  Contact Information  
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-indigo-500" />
                    <span>{consumer?.email}</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-indigo-500" />
                    <span>{consumer?.contactNumber}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-indigo-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-indigo-700">
                        Service Address:
                      </div>
                      <div className="whitespace-pre-line">
                        {formatTerritoryAddress(
                          consumer?.territoryData,
                          "service"
                        ) || "Not available"}
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-indigo-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-indigo-700">
                        Billing Address:
                      </div>
                      <div className="whitespace-pre-line">
                        {formatTerritoryAddress(
                          consumer?.territoryData,
                          "billing"
                        ) || "Not available"}
                      </div>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Account Information Card */}
            <Card className="shadow-md bg-gradient-to-br from-green-50 to-teal-50 border border-green-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-700">
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Account Number</span>
                    <span>{consumer?.consumerNo}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Category</span>
                    <span>{consumer?.categoryDisplay}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Sub Category</span>
                    <span>{consumer?.subCategoryDisplay}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Connection Date</span>
                    <span>{consumer?.connectDate}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Plan</span>
                    <span>{consumer?.planDetails?.planName}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Payment Details Card */}
            <Card className="shadow-md bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-700">
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Last Payment</span>
                    <span>
                      {CURRENCY}
                      {consumer?.billData?.lastPayAmount}
                    </span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Payment Date</span>
                    <span>{consumer?.billData?.lastPaymentDate}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Payment Mode</span>
                    <span>{consumer?.billData?.paymentModeDisplay}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Next Payment Due</span>
                    <span>{consumer?.billData?.dueDate}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Outstanding</span>
                    <span>
                      {CURRENCY}
                      {consumer?.billData?.outstandingBalance}
                    </span>
                  </li>
                  {/* <li className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">Payment Compliance</span>
                    <span>{consumer.paymentCompliance}%</span>
                  </li> */}
                </ul>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsumerDetail;
